import React from 'react';

import { ClassNameMap, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppointmentRecurring, PublicAppointment } from '@uplift/api';
import { TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useProviderPublicMatchingProfile } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { UserImage } from '@uplift/ui-v2';
import { abbreviateMonth, abbreviateWeekday } from '@uplift/utils';

export type YourProviderBannerProps = {
  classes?: Partial<ClassNameMap<'root' | 'yourProvider'>>;
} & YourProviderProps;

export const YourProviderBanner: React.VFC<YourProviderBannerProps> = props => {
  const { classes } = useYourProviderBannerStyles(props, { props });

  if (!props.providerId) {
    return null;
  }

  return (
    <div className={classes.root}>
      <YourProvider {...props} className={classes.yourProvider} />
    </div>
  );
};

const useYourProviderBannerStyles = makeStyles<YourProviderBannerProps>({ name: 'YourProviderBanner' })(theme => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    boxSizing: 'border-box',
  },
  yourProvider: {
    width: '100%',
    maxWidth: `calc(640px + ${theme.spacing(4)})`,
    margin: '0 auto',
  }
}));

export type YourProviderProps = {
  className?: string;
  providerId: string;
  appointment?: PublicAppointment;
  variant?: 'default' | 'large';
};

export const YourProvider: React.VFC<YourProviderProps> = ({ variant = 'default', ...props }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes, cx } = useYourProviderStyles();
  const { data: matchingProfile } = useProviderPublicMatchingProfile(props.providerId);

  const appointmentSlot = React.useMemo(() => {
    if (!props.appointment) {
      return null;
    }

    const dateFrom = CalendarDateService.date(props.appointment.fromDatetime).tz(timezone);
    const dateTo = CalendarDateService.date(props.appointment.toDatetime).tz(timezone);
    const recurring = props.appointment.recurring;
    const subtitles = makeSubtitleLines(dateFrom, dateTo);

    return { dateFrom, dateTo, subtitles, recurring };
  }, [ props.appointment, timezone ]);


  return (
    <div className={cx(classes.root, props.className)}>
      <UserImage
        imageUrl={matchingProfile?.avatarAsset?.url}
        maxWidth={variant === 'default' ? '70px' : '111px'}
      />
      <div>
        <Typography className={classes.name}>{matchingProfile?.nameDisplay?.defaultDisplay}</Typography>
        {appointmentSlot && (
          <Typography className={classes.time}>
            {variant === 'default'
              ? makeSubtitle(appointmentSlot.dateFrom, appointmentSlot.dateTo)
              : <>{appointmentSlot.subtitles.line1}<br />{appointmentSlot.subtitles.line2}</>
            }
          </Typography>
        )}
        {appointmentSlot?.recurring && appointmentSlot?.recurring !== AppointmentRecurring.none && (
          <Typography className={classes.interval}>Sessions repeat {l10n.matching.recurring[appointmentSlot?.recurring]}</Typography>
        )}
      </div>
    </div>
  );
};

const useYourProviderStyles = makeStyles({ name: 'YourProvider' })(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  name: {
    fontWeight: 600,
  },
  time: {
    color: theme.palette.secondary.main,
    fontWeight: 500,
  },
  interval: {
    fontSize: '14px',
    fontWeight: 450,
  }
}));

const makeSubtitle = (fromDatetime: DateObject, toDatetime: DateObject) => {
  if (!fromDatetime?.isValid() || !toDatetime?.isValid()) {
    return '';
  }

  const selectedDayDigit = fromDatetime.format('D');
  const from = fromDatetime.format(TimeFormats['h:mm']);

  const to = toDatetime.format(TimeFormats['h:mm a']);
  const tzName = fromDatetime.format('z');
  const timeRange = `${from} – ${to} ${tzName}`;

  return `${abbreviateWeekday(fromDatetime)}, ${abbreviateMonth(
    fromDatetime
  )} ${selectedDayDigit}, ${timeRange}`;
};

const makeSubtitleLines = (fromDatetime: DateObject, toDatetime: DateObject) => {
  if (!fromDatetime?.isValid() || !toDatetime?.isValid()) {
    return null;
  }

  const selectedDayDigit = fromDatetime.format('D');
  const from = fromDatetime.format(TimeFormats['h:mm']);

  const to = toDatetime.add(-10, 'minutes').format(TimeFormats['h:mm a']);
  const tzName = fromDatetime.format('z');
  const timeRange = `${from} – ${to} ${tzName}`;

  return {
    line1: `${abbreviateWeekday(fromDatetime)}, ${abbreviateMonth(fromDatetime)} ${selectedDayDigit}`,
    line2: timeRange
  };
};
